import { useEffect, useId, useRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Button, Flex, Icon, Text } from '@nula/core-ui';
import { EVENT_PREFERENCE, templateAvailability, type TemplateSummary } from '@/lib/templates/catalog';
import type { SurfaceSource } from '@/lib/sync/document-surfaces';
import {
  addedDocumentsFrom, documentSourceOption, initialTicks, toggleSource, type TemplateSourceOption,
} from '../utils/template-sources';
import { SOURCE_KIND_ICON, SurfaceSourcesList } from './surface-sources-list';
import { SurfaceDocumentPickerFace, type PickedDocument } from './surface-document-picker-face';
import { TemplateOwnerTag } from './template-owner-tag';
import styles from './surface-template-face.module.css';

interface SurfaceTemplateFaceProps {
  template: TemplateSummary;
  sources: readonly TemplateSourceOption[];
  /** Ticked on open where still offered, else the best match (ui-design.md §4.5). */
  initialSources?: readonly SurfaceSource[] | null;
  refusal: string | null;
  busy: boolean;
  pending?: boolean;
  onGenerate: (sources: TemplateSourceOption[]) => void;
  onBack: () => void;
}

const back = cva(styles.back);

const NOTHING_TO_READ = { hasBodyText: false, hasSurfaceText: false, finishedRecordingIds: [] };

export function SurfaceTemplateFace({
  template, sources, initialSources = null, refusal, busy, pending = false, onGenerate, onBack,
}: SurfaceTemplateFaceProps) {
  const [added, setAdded] = useState<TemplateSourceOption[]>(() => addedDocumentsFrom(sources, initialSources));
  const preference = EVENT_PREFERENCE[template.id] ?? 'upcoming';
  const [ticked, setTicked] = useState<string[]>(() => initialTicks(
    [...sources, ...added],
    initialSources,
    preference,
  ));
  // Linked meetings can arrive after the face opens; until the person ticks,
  // adds something or presses Generate, the default ticks follow the options
  // as they arrive.
  const [touched, setTouched] = useState(false);
  const optionIds = sources.map((o) => o.id).join('\n');
  const [ticksFor, setTicksFor] = useState(optionIds);
  if (optionIds !== ticksFor) {
    setTicksFor(optionIds);
    if (!touched) setTicked(initialTicks([...sources, ...added], initialSources, preference));
  }
  const [picking, setPicking] = useState(false);
  const backRef = useRef<HTMLButtonElement>(null);
  const options = [...sources, ...added];
  const tickedSources = options.filter((o) => ticked.includes(o.id));
  const blocked = refusal !== null || tickedSources.length === 0 || pending;
  const canAdd = template.sourceKinds.includes('document');
  const listed = sources.length > 1 || (canAdd && sources.length > 0);
  const single = sources.length === 1 ? sources[0] : undefined;
  const passagePreview = sources.find((o) => o.kind === 'passage')?.preview;
  const showSections = template.outputType !== 'canvas' && template.sections.length > 0;
  const nothingToReadId = useId();
  const availability = sources.length === 0 ? templateAvailability(template, NOTHING_TO_READ) : null;
  const nothingToRead = availability?.available === false ? availability.reason : null;

  // A refusal disables the focused Generate, and the browser drops focus to
  // <body> — outside the panel's keyboard handling.
  useEffect(() => {
    if (refusal !== null) backRef.current?.focus();
  }, [refusal]);

  const toggle = (id: string) => {
    setTouched(true);
    if (added.some((o) => o.id === id)) {
      setAdded((current) => current.filter((o) => o.id !== id));
      setTicked((current) => current.filter((t) => t !== id));
      return;
    }
    setTicked((current) => toggleSource(current, id));
  };

  const addDocument = (document: PickedDocument) => {
    const option = documentSourceOption(document);
    setTouched(true);
    setAdded((current) => [...current, option]);
    setTicked((current) => toggleSource(current, option.id));
    setPicking(false);
  };

  if (picking) {
    const exclude = new Set(options.flatMap((o) => ('entityId' in o.request ? [o.request.entityId] : [])));
    return (
      <SurfaceDocumentPickerFace exclude={exclude} onPick={addDocument} onBack={() => setPicking(false)} />
    );
  }

  return (
    <Flex
      vertical
      className={styles.templateFace}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        onBack();
      }}
    >
      <button
        ref={backRef}
        type="button"
        role="menuitem"
        aria-label={`Back to templates, ${template.name}`}
        onClick={onBack}
        className={back()}
      >
        <Flex align="center" justify="center" className={styles.backIcon} aria-hidden="true">
          <Icon name="arrow_back" size={16} />
        </Flex>
        <Text className={styles.title}>{template.name}</Text>
        <TemplateOwnerTag owner={template.owner} className={styles.owner} />
      </button>

      <Flex vertical className={styles.content}>
        <Flex vertical className={styles.body}>
          <Text type="secondary" className={styles.description}>{template.description}</Text>
        </Flex>

        <Text className={styles.label}>{listed ? 'Sources' : 'Source'}</Text>
        <Flex vertical className={styles.body}>
          {listed ? (
            <SurfaceSourcesList
              options={options}
              ticked={ticked}
              onToggle={toggle}
              onAdd={canAdd ? () => setPicking(true) : undefined}
            />
          ) : nothingToRead ? (
            <Text id={nothingToReadId} className={styles.nothingToRead}>{nothingToRead}</Text>
          ) : single && (
            <Flex align="center" className={styles.chip}>
              <Icon name={SOURCE_KIND_ICON[single.kind]} size={16} className={styles.chipIcon} />
              <Text className={styles.chipTitle}>{single.name}</Text>
              {single.kind === 'page' && <Text className={styles.faint}>{`· ${single.kindLabel}`}</Text>}
            </Flex>
          )}
          {passagePreview && (
            <Text italic className={styles.passagePreview}>{`“${passagePreview}”`}</Text>
          )}
        </Flex>

        {showSections && (
          <>
            <Text className={styles.label}>Sections</Text>
            <Flex vertical role="list" className={clsx(styles.body, styles.sections)}>
              {template.sections.map((section, index) => (
                <Flex key={section.key} role="listitem" align="baseline" className={styles.section}>
                  <Text aria-hidden="true" className={styles.number}>{`${index + 1}.`}</Text>
                  <Text>{section.heading}</Text>
                  {!section.required && <Text className={styles.faint}>if any</Text>}
                </Flex>
              ))}
            </Flex>
          </>
        )}
      </Flex>

      <Flex vertical className={styles.footer}>
        {refusal && <Text role="alert" className={styles.refusal}>{refusal}</Text>}
        <Button
          autoFocus
          variant="primary"
          block
          className={styles.generate}
          loading={busy}
          disabled={blocked}
          aria-describedby={nothingToRead ? nothingToReadId : undefined}
          onClick={() => {
            setTouched(true);
            if (tickedSources.length > 0) onGenerate(tickedSources);
          }}
        >
          Generate
          {!blocked && <Text aria-hidden="true" className={styles.enterHint}>↵</Text>}
        </Button>
      </Flex>
    </Flex>
  );
}
